import { 
  LocalDistrict, 
  LocalSector, 
  LocalHighlights, 
  SupportedLocation,
  CountryRecord,
  RegionRecord 
} from "../data/locations/types";
import { SUPPORTED_COUNTRIES, getCountryByCodeOrSlug } from "../data/locations/countriesAndRegions";
import { US_LOCATIONS } from "../data/locations/usLocations";
import { CANADA_LOCATIONS } from "../data/locations/canadaLocations";
import { MEXICO_LOCATIONS } from "../data/locations/mexicoLocations";
import { INTERNATIONAL_LOCATIONS } from "../data/locations/internationalLocations";

export type { LocalDistrict, LocalSector, LocalHighlights, SupportedLocation, CountryRecord, RegionRecord };

export const MIN_DATA_THRESHOLD = {
  minCoverageScore: 70,
  minBusinessCount: 10,
  minCategoryCount: 5,
};

/**
 * Validates whether a location meets all quality, data, and indexing criteria to be publicly published & indexed.
 */
export function isLocationQualified(loc: SupportedLocation | null | undefined): boolean {
  if (!loc) return false;
  return (
    loc.pageEnabled &&
    loc.indexingStatus === "indexed" &&
    loc.dataCoverageScore >= MIN_DATA_THRESHOLD.minCoverageScore &&
    loc.businessCount >= MIN_DATA_THRESHOLD.minBusinessCount &&
    loc.relevantCategoryCount >= MIN_DATA_THRESHOLD.minCategoryCount &&
    Boolean(loc.citySlug)
  );
}

/**
 * Master Unified Location Registry for BizNearYou Worldwide Coverage
 */
export const LOCATION_REGISTRY: SupportedLocation[] = [
  ...US_LOCATIONS,
  ...CANADA_LOCATIONS,
  ...MEXICO_LOCATIONS,
  ...INTERNATIONAL_LOCATIONS
];

/**
 * Retrieves a location record by its normalized canonical city slug.
 */
export function getLocationBySlug(slug: string): SupportedLocation | null {
  if (!slug) return null;
  const cleanSlug = slug.toLowerCase().trim();
  const found = LOCATION_REGISTRY.find(loc => loc.citySlug.toLowerCase() === cleanSlug);
  return found || null;
}

/**
 * Returns all published locations that meet the qualification threshold.
 */
export function getAllQualifiedLocations(): SupportedLocation[] {
  return LOCATION_REGISTRY.filter(isLocationQualified);
}

/**
 * Returns all supported countries with active published locations.
 */
export function getAllSupportedCountries(): CountryRecord[] {
  const qualified = getAllQualifiedLocations();
  return SUPPORTED_COUNTRIES.map(country => {
    const count = qualified.filter(
      loc => loc.countryCode === country.countryCode || loc.countrySlug === country.countrySlug
    ).length;
    return {
      ...country,
      locationCount: count
    };
  }).filter(c => c.locationCount > 0);
}

/**
 * Returns a country record by code or slug.
 */
export function getCountryBySlug(countrySlugOrCode: string): CountryRecord | null {
  return getCountryByCodeOrSlug(countrySlugOrCode);
}

/**
 * Returns all supported states/regions/provinces with location counts.
 */
export function getAllSupportedStates(): Array<{ 
  stateName: string; 
  stateAbbreviation: string; 
  locationCount: number;
  countryName: string;
  countryCode: string;
  countrySlug: string;
  regionSlug: string;
}> {
  const map = new Map<string, { 
    stateName: string; 
    stateAbbreviation: string; 
    locationCount: number;
    countryName: string;
    countryCode: string;
    countrySlug: string;
    regionSlug: string;
  }>();

  getAllQualifiedLocations().forEach(loc => {
    const key = `${loc.countryCode}-${loc.stateAbbreviation}`.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.locationCount++;
    } else {
      map.set(key, {
        stateName: loc.stateName,
        stateAbbreviation: loc.stateAbbreviation,
        locationCount: 1,
        countryName: loc.countryName,
        countryCode: loc.countryCode,
        countrySlug: loc.countrySlug,
        regionSlug: loc.regionSlug
      });
    }
  });

  return Array.from(map.values()).sort((a, b) => a.stateName.localeCompare(b.stateName));
}

/**
 * Neutral alias for getAllSupportedStates
 */
export const getAllSupportedRegions = getAllSupportedStates;

/**
 * Formats a region display name with country context.
 * E.g., "Illinois, United States" or "Ontario, Canada"
 */
export function formatRegionWithCountry(stateName: string, countryName: string): string {
  if (!stateName) return countryName || "";
  if (!countryName) return stateName;
  return `${stateName}, ${countryName}`;
}

/**
 * Formats a location full display name with region and country.
 * E.g., "Peoria, IL (United States)" or "Toronto, ON (Canada)"
 */
export function formatLocationFullDisplayName(loc: SupportedLocation): string {
  if (!loc) return "";
  return `${loc.cityName}, ${loc.stateAbbreviation} (${loc.countryName})`;
}

/**
 * Returns regions within a specific country.
 */
export function getRegionsByCountry(countrySlugOrCode: string): Array<{
  stateName: string;
  stateAbbreviation: string;
  locationCount: number;
  countryName: string;
  countryCode: string;
  countrySlug: string;
  regionSlug: string;
}> {
  const clean = countrySlugOrCode.toLowerCase().trim();
  const country = getCountryByCodeOrSlug(clean);
  const targetCode = country ? country.countryCode : clean.toUpperCase();

  return getAllSupportedStates().filter(
    st => st.countryCode.toLowerCase() === targetCode.toLowerCase() || st.countrySlug.toLowerCase() === clean
  );
}

/**
 * Finds state/region details by state slug or state code.
 */
export function getStateBySlug(stateSlug: string): { 
  stateName: string; 
  stateAbbreviation: string; 
  locationCount: number;
  countryName: string;
  countryCode: string;
  countrySlug: string;
  regionSlug: string;
} | null {
  if (!stateSlug) return null;
  const clean = stateSlug.toLowerCase().trim();
  const states = getAllSupportedStates();

  for (const st of states) {
    const slugName = st.stateName.toLowerCase().replace(/\s+/g, "-");
    const slugAbbr = st.stateAbbreviation.toLowerCase();
    const regSlug = st.regionSlug ? st.regionSlug.toLowerCase() : "";
    
    if (clean === slugName || clean === slugAbbr || clean === regSlug) {
      return st;
    }
  }
  return null;
}

/**
 * Returns qualified location records for a given state/region abbreviation or slug.
 */
export function getQualifiedLocationsByState(stateAbbreviationOrSlug: string): SupportedLocation[] {
  if (!stateAbbreviationOrSlug) return [];
  const clean = stateAbbreviationOrSlug.toLowerCase().trim();
  return getAllQualifiedLocations().filter(loc => 
    loc.stateAbbreviation.toLowerCase() === clean ||
    loc.stateName.toLowerCase().replace(/\s+/g, "-") === clean ||
    (loc.regionSlug && loc.regionSlug.toLowerCase() === clean)
  );
}

/**
 * Returns nearby qualified location records for a given city slug.
 */
export function getNearbyQualifiedLocations(slug: string, limit: number = 6): SupportedLocation[] {
  const current = getLocationBySlug(slug);
  if (!current) return [];

  const qualifiedSlugs = new Set(getAllQualifiedLocations().map(l => l.citySlug));
  const result: SupportedLocation[] = [];

  // 1. First pick from defined nearbyLocations array if qualified
  for (const nSlug of current.nearbyLocations) {
    if (nSlug !== current.citySlug && qualifiedSlugs.has(nSlug)) {
      const loc = getLocationBySlug(nSlug);
      if (loc && !result.some(r => r.citySlug === loc.citySlug)) {
        result.push(loc);
      }
    }
  }

  // 2. If we need more, calculate distance to other qualified locations in the same country
  if (result.length < limit) {
    const candidates = getAllQualifiedLocations().filter(
      l => l.citySlug !== current.citySlug && 
           l.countryCode === current.countryCode &&
           !result.some(r => r.citySlug === l.citySlug)
    );

    // Calculate Haversine distance
    const withDistance = candidates.map(c => {
      const R = 3958.8; // miles
      const dLat = (c.lat - current.lat) * Math.PI / 180;
      const dLon = (c.lng - current.lng) * Math.PI / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(current.lat * Math.PI / 180) * Math.cos(c.lat * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return { loc: c, dist };
    });

    withDistance.sort((a, b) => a.dist - b.dist);

    for (const item of withDistance) {
      if (result.length >= limit) break;
      result.push(item.loc);
    }
  }

  return result.slice(0, limit);
}

// Conceptual URL Helpers
export function getJobsLocationUrl(citySlug: string): string {
  const loc = getLocationBySlug(citySlug);
  const slug = loc ? loc.citySlug : citySlug.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  return `/jobs/${slug}`;
}

export function getHiringLocationUrl(citySlug: string): string {
  const loc = getLocationBySlug(citySlug);
  const slug = loc ? loc.citySlug : citySlug.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  return `/businesses-hiring/${slug}`;
}

export function getInternshipsLocationUrl(citySlug: string): string {
  const loc = getLocationBySlug(citySlug);
  const slug = loc ? loc.citySlug : citySlug.toLowerCase().replace(/[^a-z0-9-]/g, "-");
  return `/internships/${slug}`;
}
